<template>
    <div class="ai-parameter-select">
        <RedesignBaseSelect
            :value="value"
            :filterable="filterable"
            :multiple="multiple"
            :collapse-tags="collapseTags"
            :placeholder="placeholder"
            :clearable="clearable"
            popper-class="redesign-select-group-dropdown"
            @input="handleInput"
        >
            <template #label>
                {{ label }}
            </template>

            <!-- Grouped options -->
            <el-option-group
                v-for="group in parameterGroups"
                :key="group.label"
                :label="group.label"
            >
                <el-option
                    v-for="item in group.options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                />
            </el-option-group>

            <!-- Custom parameters group -->
            <el-option-group
                v-if="customParameters.length"
                :label="$t('common.customParameters')"
            >
                <el-option
                    v-for="item in customParameters"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                />
            </el-option-group>
        </RedesignBaseSelect>

        <!-- Draggable selected items list - only for multiple mode with more than 1 item -->
        <div
            v-if="multiple && canReorder"
            class="selected-items-container mt-3"
        >
            <div class="selected-items-header text-xs text-gray-500 mb-2">
                {{ $t('common.dragToReorder') }}
            </div>
            <draggable
                v-model="draggableList"
                :animation="200"
                class="selected-items-list"
                @change="handleDragChange"
            >
                <div
                    v-for="item in draggableList"
                    :key="item.value"
                    class="selected-item"
                >
                    <span class="drag-handle">
                        <i class="vc-icon-drag" />
                    </span>
                    <span class="item-label">{{ item.label }}</span>
                    <span
                        class="remove-btn"
                        @click.stop="removeItem(item.value)"
                    >
                        ×
                    </span>
                </div>
            </draggable>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Option as ElOption, OptionGroup as ElOptionGroup } from 'element-ui'
import draggable from 'vuedraggable'

import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import { TranslateResult } from 'vue-i18n'

export interface ParameterOption {
    label: string
    value: string
}

export interface ParameterGroup {
    label: string
    options: ParameterOption[]
}

const props = withDefaults(defineProps<{
    value: string | string[]
    label: string | TranslateResult
    placeholder?: string | TranslateResult
    parameterGroups: ParameterGroup[]
    customParameters?: ParameterOption[]
    multiple?: boolean
    filterable?: boolean
    collapseTags?: boolean
    clearable?: boolean
}>(), {
    placeholder: '',
    customParameters: () => [],
    multiple: false,
    filterable: true,
    collapseTags: true,
    clearable: false
})

const emit = defineEmits<{
    (e: 'input', value: string | string[]): void
}>()

// All available options flattened
const allOptions = computed<ParameterOption[]>(() => {
    const options: ParameterOption[] = []
    props.parameterGroups.forEach(group => {
        options.push(...group.options)
    })
    options.push(...props.customParameters)
    return options
})

// Get label for a value
function getLabelForValue (value: string): string {
    const option = allOptions.value.find(o => o.value === value)
    return option?.label || value
}

// Mutable list for draggable (vuedraggable requires a mutable array)
const draggableList = ref<ParameterOption[]>([])

// Sync draggableList with props.value
watch(
    () => props.value,
    (newValue) => {
        if (props.multiple && Array.isArray(newValue)) {
            draggableList.value = newValue.map(v => ({
                value: v,
                label: getLabelForValue(v)
            }))
        } else {
            draggableList.value = []
        }
    },
    { immediate: true, deep: true }
)

// Check if there are enough items to reorder (more than 1)
const canReorder = computed(() => draggableList.value.length > 1)

function handleInput (val: string | string[]) {
    emit('input', val)
}

function handleDragChange () {
    // Emit new order after drag completes
    if (props.multiple) {
        emit('input', draggableList.value.map(item => item.value))
    }
}

function removeItem (valueToRemove: string) {
    if (props.multiple && Array.isArray(props.value)) {
        const newValue = props.value.filter(v => v !== valueToRemove)
        emit('input', newValue)
    }
}
</script>

<style lang="scss" scoped>
.ai-parameter-select {
    max-width: 400px;

    :deep(.base-select) {
        .el-select {
            width: 100%;
            max-width: 360px;
        }
    }
}

.selected-items-container {
    max-width: 360px;

    .selected-items-header {
        @apply font-medium;
        color: var(--steel);
    }
}

.selected-items-list {
    @apply flex flex-col gap-1.5;
}

.selected-item {
    @apply flex items-center gap-2 cursor-grab;
    padding: 8px 10px;
    background: var(--mainBgColor-color);
    border: 1px solid var(--ui-lines);
    border-radius: 6px;
    transition: all 0.15s ease;
    user-select: none;

    &:hover {
        border-color: var(--primary-actions);
        background: color-mix(
                in srgb,
                var(--backgroundLight-color) 90%,
                var(--primary-color) 10%
        );
    }

    &:active {
        @apply cursor-grab;
    }

    .drag-handle {
        @apply flex items-center;
        color: var(--steel);

        i {
            font-size: 14px;
        }
    }

    .item-label {
        @apply text-xs;
        flex: 1;
    }

    .remove-btn {
        @apply flex items-center justify-center cursor-pointer;
        color: var(--steel);
        width: 20px;
        height: 20px;
        border-radius: 50%;
        transition: all 0.15s ease;

        &:hover {
            background: var(--primary-actions);
            color: white;
        }
    }
}
</style>
